import React from 'react';
import { Box, Typography, useTheme, CircularProgress, Alert } from '@mui/material';
import { AdaptiveContainer } from './index';
import UpdateCard from './UpdateCard';
import useUpdates from '../../hooks/useUpdates';
import API_CONFIG from '../../config/api';
import { useTranslations } from '../../hooks/useTranslations';

const UpdatesSection = ({
  title,
  updates: initialUpdates = [],
  backgroundColor,
  cardBackground,
  titleColor,
  enableApi = false,
  useDirectUrl = false, // Option pour utiliser l'URL directe
  ...props
}) => {
  const theme = useTheme();
  const { t } = useTranslations();
  
  // Construire l'URL avec le paramètre type=message pour filtrer les FAQ
  const apiUrl = useDirectUrl 
    ? `${API_CONFIG.baseURL}/api/getStaticData`
    : `${API_CONFIG.baseURL}/api/getStaticData`;
  
    // console.log('UpdatesSection - useDirectUrl:', useDirectUrl);
    
  // Utiliser le hook pour récupérer les mises à jour depuis l'API
  const { updates: apiUpdates, loading, error, refresh } = useUpdates(
    apiUrl,
    { enableApi }
  );

  // Utiliser les données de l'API si disponibles, sinon les données initiales
  const allUpdates = enableApi && apiUpdates.length > 0 ? apiUpdates : initialUpdates;
  
  // Filtrer pour ne garder que les éléments de type "message" (exclure les FAQ)
  const updates = allUpdates.filter(update => {
    // Si le champ type n'existe pas, on considère que c'est un message (rétrocompatibilité)
    // Si le type existe, on ne garde que "message"
    return !update.type || update.type === 'message';
  });

  return (
    <Box sx={{ 
      backgroundColor: backgroundColor || theme.palette.background.default
    }} {...props}>
      <AdaptiveContainer>
        <Box
          sx={{
            backgroundColor: cardBackground || theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: theme.shadows[4],
            overflow: "hidden",
            width: "100%",
          }}
        >
          {/* Contenu de la section */}
          <Box sx={{ 
            p: { xs: 3, md: 4, lg: 4 },
          }}>
            
            {/* Titre avec icône */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  // backgroundColor: '#B32535',
                  border: '2px solid #B32535',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  color: '#B32535',
                  fontSize: '24px',
                  fontWeight: 'bold'
                }}
              >
                !
              </Box>
              <Typography
                variant="h4"
                component="h2"
                sx={{ 
                  fontWeight: 600, 
                  color: titleColor || theme.palette.primary.main,
                  fontSize: { xs: '1.5rem', md: '1.75rem' }
                }}
              >
                {title}
              </Typography>
            </Box>

            {/* État de chargement */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Message d'erreur */}
            {error && (
              <Alert 
                severity={theme.palette.error.main} 
                sx={{ mb: 2 }}
                action={
                  <Box
                    component="button"
                    onClick={refresh}
                    sx={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '0.875rem'
                    }}
                  >
                    {t("updates.retry")}
                  </Box>
                }
              >
                {t("updates.errorLoading", { error })}
              </Alert>
            )}

            {/* Liste des updates */}
            {!loading && (
              <Box>
                {updates.length > 0 ? (
                  updates.map((update, index) => (
                    <UpdateCard
                      key={update.id || index}
                      date={update.date}
                      title={update.title}
                      subtitle={update.subtitle}
                      description={update.description}
                      linkText={update.linkText}
                      linkHref={update.link}
                      variant={update.variant || 'default'}
                      sx={{ 
                        mb: index === updates.length - 1 ? 0 : 3 // Pas de marge sur le dernier élément
                      }}
                    />
                  ))
                ) : !loading && (
                  !error && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 2 }}
                    >
                      {t("updates.noUpdates")}
                    </Typography>
                  )
                )}
              </Box>
            )}
          </Box>
        </Box>
      </AdaptiveContainer>
    </Box>
  );
};

export default UpdatesSection; 